import { useCallback, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export interface UseCalendarUrlReturn {
  calendarUrl: string | undefined
  isLoading: boolean
  error: string | null
  generateCalendarUrl: () => Promise<void>
  copyToClipboard: () => Promise<boolean>
}

export const useCalendarUrl = (): UseCalendarUrlReturn => {
  const { session } = useAuth()
  const [calendarUrl, setCalendarUrl] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateCalendarUrl = useCallback(async (): Promise<void> => {
    if (!session) {
      setError('User not authenticated')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: functionError } = await supabase.functions.invoke('caldav', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token || ''}`,
          'Content-Type': 'application/json',
        },
      })

      if (functionError) {
        console.error('Error generating calendar URL:', functionError)
        throw functionError
      }

      if (data?.calendar_url) {
        setCalendarUrl(data.calendar_url)
      } else {
        throw new Error('No calendar URL returned')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate calendar URL'
      console.error('Failed to generate calendar URL:', err)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [session])

  const copyToClipboard = useCallback(async (): Promise<boolean> => {
    if (!calendarUrl) {
      setError('No calendar URL to copy')
      return false
    }

    try {
      await navigator.clipboard.writeText(calendarUrl)
      return true
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      setError('Failed to copy to clipboard')
      return false
    }
  }, [calendarUrl])

  return {
    calendarUrl,
    isLoading,
    error,
    generateCalendarUrl,
    copyToClipboard,
  }
}
